import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

// Team images
import teamHeroImage from '../../assets/images/team-hero-IMG_1021-Edit-1.jpg';
import missionBackground from '../../assets/images/mission-background-hvac-professional.jpg';
import visionBackground from '../../assets/images/vision-background-suburban.jpg';

import kevinHeadshotGif from '../../assets/images/kevin-hart-headshot.gif';
import kevinHeadshotStatic from '../../assets/images/kevin-hart-static.jpg';
import lorneHeadshotGif from '../../assets/images/lorne-sturtevant-headshot.jpg';
import lorneHeadshotStatic from '../../assets/images/lorne-sturtevant-static.jpg';
import teamPhoto1 from '../../assets/images/team-photo-1.png';
import teamPhoto2 from '../../assets/images/team-photo-2.png';
import teamPhoto3 from '../../assets/images/team-photo-3.png';

// Team gallery images
import teamLucyMain from '../../assets/images/team-lucy-main.jpg';
import teamLucyGif from '../../assets/images/team-lucy-gif.gif';
import teamChristinaMain from '../../assets/images/team-christina-main.jpg';
import teamArminMain from '../../assets/images/team-armin-main.jpg';
import teamLubovMain from '../../assets/images/team-lubov-main.jpg';
import teamLubovGif from '../../assets/images/team-lubov-gif.gif';
import teamJesseMain from '../../assets/images/team-jesse-main.jpg';
import teamRaminMain from '../../assets/images/team-ramin-main.jpg';
import teamRaminGif from '../../assets/images/team-ramin-gif.gif';
import teamPhilMain from '../../assets/images/team-phil-main.jpg';
import teamPhilGif from '../../assets/images/team-phil-gif.gif';
import teamSemyonMain from '../../assets/images/team-semyon-main.jpg';
import teamSemyonGif from '../../assets/images/team-semyon-gif.gif';
import teamJeelMain from '../../assets/images/team-jeel-main.jpg';
import teamJeelGif from '../../assets/images/team-jeel-gif.gif';
import teamMarilynMain from '../../assets/images/team-marilyn-main.jpg';
import teamMarilynGif from '../../assets/images/team-marilyn-gif.gif';

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  height: '60vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${teamHeroImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: 'white',
  textAlign: 'center',
}));

const SectionWithBackground = styled(Box)<{ bgImage?: string; isVision?: boolean }>(({ theme, bgImage, isVision }) => ({
  minHeight: '600px',
  display: 'flex',
  alignItems: 'center',
  backgroundImage: bgImage ? (
    isVision 
      ? `linear-gradient(to right, rgba(69, 90, 100, 0.4), rgba(69, 90, 100, 0.99)), url(${bgImage})` 
      : `linear-gradient(to right, rgba(0, 196, 193, 0.9), rgba(0, 196, 193, 0.3)), url(${bgImage})`
  ) : 'none',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: 'white',
  padding: theme.spacing(12, 0),
}));

const TeamCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: theme.shadows[10],
  },
}));

const TeamPhotoContainer = styled(Box)({
  position: 'relative',
  width: '280px',
  height: '280px',
  margin: '0 auto 16px',
  borderRadius: '50%',
  overflow: 'hidden',
  cursor: 'pointer',
  '&:hover .hover-image': {
    opacity: 1,
  },
});

const TeamPhoto = styled('img')({
  width: '90%',
  height: '90%',
  borderRadius: '50%',
  objectFit: 'cover',
  objectPosition: 'center 25%',
  position: 'absolute',
  top: '5%',
  left: '5%',
  transition: 'opacity 0.3s ease',
});

const TeamPhotoHover = styled('img')({
  width: '90%',
  height: '90%',
  borderRadius: '50%',
  objectFit: 'cover',
  objectPosition: 'center 25%',
  position: 'absolute',
  top: '5%',
  left: '5%',
  opacity: 0,
  transition: 'opacity 0.3s ease',
});

const SpinningFanContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '32px',
});

const SpinningFan = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  position: 'relative',
  animation: 'spin 2s linear infinite',
  transformOrigin: '40px 40px', // Exact center coordinates
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '36px',
    left: '36px',
    width: '8px',
    height: '8px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    zIndex: 5,
  },
  '& .blade': {
    position: 'absolute',
    top: '37px',
    left: '44px',
    width: '0',
    height: '0',
    transformOrigin: '-4px 3px', // Rotate around the center hub
    '&:nth-of-type(1)': {
      transform: 'rotate(0deg)',
      borderLeft: '30px solid transparent',
      borderRight: '4px solid transparent',
      borderBottom: `6px solid ${theme.palette.primary.main}`,
    },
    '&:nth-of-type(2)': {
      transform: 'rotate(72deg)',
      borderLeft: '30px solid transparent',
      borderRight: '4px solid transparent',
      borderBottom: `6px solid ${theme.palette.primary.main}`,
    },
    '&:nth-of-type(3)': {
      transform: 'rotate(144deg)',
      borderLeft: '30px solid transparent',
      borderRight: '4px solid transparent',
      borderBottom: `6px solid ${theme.palette.primary.main}`,
    },
    '&:nth-of-type(4)': {
      transform: 'rotate(216deg)',
      borderLeft: '30px solid transparent',
      borderRight: '4px solid transparent',
      borderBottom: `6px solid ${theme.palette.primary.main}`,
    },
    '&:nth-of-type(5)': {
      transform: 'rotate(288deg)',
      borderLeft: '30px solid transparent',
      borderRight: '4px solid transparent',
      borderBottom: `6px solid ${theme.palette.primary.main}`,
    },
  },
}));

const TeamGalleryContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '24px',
  marginTop: '32px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  },
}));

const TeamGalleryPhoto = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  paddingBottom: '100%', // This creates a square aspect ratio
  borderRadius: '8px',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[10],
  },
  '&:hover .hover-image': {
    opacity: 1,
  },
}));

const TeamGalleryImage = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'opacity 0.3s ease',
});

const TeamGalleryImageHover = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  opacity: 0,
  transition: 'opacity 0.3s ease',
});

const AboutPage: React.FC = () => {
  const theme = useTheme();

  // Team gallery data
  const teamMembers = [
    { name: 'Lucy', mainPhoto: teamLucyMain, gifPhoto: teamLucyGif },
    { name: 'Christina', mainPhoto: teamChristinaMain, gifPhoto: null },
    { name: 'Armin', mainPhoto: teamArminMain, gifPhoto: null },
    { name: 'Lubov', mainPhoto: teamLubovMain, gifPhoto: teamLubovGif },
    { name: 'Jesse', mainPhoto: teamJesseMain, gifPhoto: null },
    { name: 'Ramin', mainPhoto: teamRaminMain, gifPhoto: teamRaminGif },
    { name: 'Phil', mainPhoto: teamPhilMain, gifPhoto: teamPhilGif },
    { name: 'Semyon', mainPhoto: teamSemyonMain, gifPhoto: teamSemyonGif },
    { name: 'Jeel', mainPhoto: teamJeelMain, gifPhoto: teamJeelGif },
    { name: 'Marilyn', mainPhoto: teamMarilynMain, gifPhoto: teamMarilynGif },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 'bold', 
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            Meet the team behind HAVEN.
          </Typography>
          <Typography variant="h5" sx={{ 
            maxWidth: '800px', 
            mx: 'auto',
            fontSize: { xs: '1.2rem', md: '1.5rem' }
          }}>
            Meet the faces behind team HAVEN, who work to bring cleaner air and healthier homes to North America with our Indoor Air Quality solutions!
          </Typography>
        </Container>
      </HeroSection>

      {/* We're More Than an IAQ Company */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <SpinningFanContainer>
            <SpinningFan>
              <Box className="blade" />
              <Box className="blade" />
              <Box className="blade" />
              <Box className="blade" />
              <Box className="blade" />
            </SpinningFan>
          </SpinningFanContainer>
          <Typography variant="h3" component="h2" sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            mb: 3
          }}>
            We're more than an IAQ company
          </Typography>
          <Typography variant="h5" sx={{ 
            color: '#535f66',
            mb: 3,
            lineHeight: 1.4,
            maxWidth: '600px',
            mx: 'auto'
          }}>
            Indoor Air Quality is a growing concern. We love solving real problems for real people.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="https://teamtzoa.bamboohr.com/jobs/"
            target="_blank"
            sx={{
              background: theme.palette.primary.main,
              color: 'white',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              '&:hover': {
                background: theme.palette.primary.dark,
              },
            }}
          >
            CAREERS AT HAVEN →
          </Button>
        </Box>
      </Container>

      {/* Team Photos Section */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <img 
                src={teamPhoto1} 
                alt="HAVEN Team 1" 
                style={{ 
                  width: '100%', 
                  maxWidth: '400px', 
                  borderRadius: '8px',
                  boxShadow: theme.shadows[4]
                }} 
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <img 
                src={teamPhoto2} 
                alt="HAVEN Team 2" 
                style={{ 
                  width: '100%', 
                  maxWidth: '400px', 
                  borderRadius: '8px',
                  boxShadow: theme.shadows[4]
                }} 
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <img 
                src={teamPhoto3} 
                alt="HAVEN Team 3" 
                style={{ 
                  width: '100%', 
                  maxWidth: '400px', 
                  borderRadius: '8px',
                  boxShadow: theme.shadows[4]
                }} 
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Our Vision Section */}
      <SectionWithBackground bgImage={visionBackground} isVision={true}>
        <Container maxWidth="xl">
          <Grid container justifyContent="flex-end">
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' }, pr: { md: 4 } }}>
                <Typography variant="h3" component="h2" sx={{ 
                  fontWeight: 'bold',
                  mb: 4,
                  color: 'white'
                }}>
                  Our Vision
                </Typography>
                <Typography variant="h5" sx={{ 
                  mb: 3,
                  lineHeight: 1.6,
                  color: 'white'
                }}>
                  Our vision is to turn homes into safe havens.
                </Typography>
                <Typography variant="h6" sx={{ 
                  mb: 3,
                  lineHeight: 1.6,
                  color: 'white',
                  fontWeight: 300
                }}>
                  We see a future where homes are clean & comfortable environments, regardless of occupant activities like cleaning and cooking.
                </Typography>
                <Typography variant="h6" sx={{ 
                  lineHeight: 1.6,
                  color: 'white',
                  fontWeight: 300
                }}>
                  We believe homes can not only become aware of these realities, they can manage them, too.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </SectionWithBackground>

      {/* Our Mission Section */}
      <SectionWithBackground bgImage={missionBackground}>
        <Container maxWidth="xl">
          <Grid container justifyContent="flex-start">
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' }, pl: { md: 4 } }}>
                <Typography variant="h3" component="h2" sx={{ 
                  fontWeight: 'bold',
                  mb: 4,
                  color: 'white'
                }}>
                  Our Mission
                </Typography>
                <Typography variant="h5" sx={{ 
                  mb: 3,
                  lineHeight: 1.6,
                  color: 'white'
                }}>
                  Our mission is to empower HVAC professionals to solve Indoor Air Quality (IAQ) issues for homeowners.
                </Typography>
                <Typography variant="h6" sx={{ 
                  lineHeight: 1.6,
                  color: 'white',
                  fontWeight: 500
                }}>
                  We're boiling a complex and intangible problem down into the tools and workflows that can be deployed by Service Companies.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </SectionWithBackground>

      {/* Our Team Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" sx={{ 
          textAlign: 'center',
          color: theme.palette.primary.main,
          fontWeight: 'bold',
          mb: 6
        }}>
          Our Team
        </Typography>
        
        <Grid container spacing={4} justifyContent="center">
          {/* Kevin Hart */}
          <Grid item xs={12} sm={6} md={4}>
            <TeamCard>
              <CardContent sx={{ p: 4 }}>
                <TeamPhotoContainer>
                  <TeamPhoto 
                    src={kevinHeadshotStatic} 
                    alt="Kevin Hart - Chief Executive Officer"
                  />
                  <TeamPhotoHover 
                    src={kevinHeadshotGif} 
                    alt="Kevin Hart - Chief Executive Officer"
                    className="hover-image"
                  />
                </TeamPhotoContainer>
                <Typography variant="h5" component="h3" sx={{ 
                  fontWeight: 'bold',
                  mb: 1,
                  color: '#455a64'
                }}>
                  Kevin Hart
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: '#455a64',
                  mb: 2,
                  fontWeight: 300
                }}>
                  Chief Executive Officer
                </Typography>
                <IconButton 
                  href="https://www.linkedin.com/in/kevinrhart/" 
                  target="_blank"
                  sx={{ color: theme.palette.primary.main }}
                >
                  <LinkedInIcon />
                </IconButton>
              </CardContent>
            </TeamCard>
          </Grid>

          {/* Lorne Sturtevant */}
          <Grid item xs={12} sm={6} md={4}>
            <TeamCard>
              <CardContent sx={{ p: 4 }}>
                <TeamPhotoContainer>
                  <TeamPhoto 
                    src={lorneHeadshotStatic} 
                    alt="Lorne Sturtevant - Lead Software Developer"
                  />
                  <TeamPhotoHover 
                    src={lorneHeadshotGif} 
                    alt="Lorne Sturtevant - Lead Software Developer"
                    className="hover-image"
                  />
                </TeamPhotoContainer>
                <Typography variant="h5" component="h3" sx={{ 
                  fontWeight: 'bold',
                  mb: 1,
                  color: '#455a64'
                }}>
                  Lorne Sturtevant
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: '#455a64',
                  mb: 2,
                  fontWeight: 300
                }}>
                  Lead Software Developer
                </Typography>
                <IconButton 
                  href="https://www.linkedin.com/in/lorne-sturtevant-07a802a/" 
                  target="_blank"
                  sx={{ color: theme.palette.primary.main }}
                >
                  <LinkedInIcon />
                </IconButton>
              </CardContent>
            </TeamCard>
          </Grid>
        </Grid>
      </Container>

      {/* Team Gallery Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" component="h2" sx={{ 
          textAlign: 'center',
          color: theme.palette.primary.main,
          fontWeight: 'bold',
          mb: 2
        }}>
          Meet Our Team
        </Typography>
        
        <TeamGalleryContainer>
          {teamMembers.map((member, index) => (
            <TeamGalleryPhoto key={index}>
              <TeamGalleryImage 
                src={member.mainPhoto} 
                alt={`${member.name} - Team Member`}
              />
              {member.gifPhoto && (
                <TeamGalleryImageHover 
                  src={member.gifPhoto} 
                  alt={`${member.name} - Team Member`}
                  className="hover-image"
                />
              )}
            </TeamGalleryPhoto>
          ))}
        </TeamGalleryContainer>
      </Container>

      {/* Career Opportunities */}
      <Box sx={{ 
        minHeight: '500px',
        display: 'flex',
        alignItems: 'center',
        backgroundImage: `linear-gradient(rgba(69, 90, 100, 0.8), rgba(69, 90, 100, 0.8)), url(${teamHeroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: 'white',
        py: 8
      }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h2" sx={{ 
            color: 'white',
            fontWeight: 'bold',
            mb: 3
          }}>
            Career Opportunities
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'white',
            mb: 2,
            lineHeight: 1.6
          }}>
            HAVEN is growing! We're looking for talented, passionate people to join our diverse team.
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'white',
            mb: 4,
            lineHeight: 1.6
          }}>
            Interested in joining our mission to make more homes into safe HAVENs?<br />
            Check out our current opportunities:
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="https://teamtzoa.bamboohr.com/jobs/"
            target="_blank"
            sx={{
              background: theme.palette.primary.main,
              color: 'white',
              fontWeight: 'bold',
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              '&:hover': {
                background: theme.palette.primary.dark,
              },
            }}
          >
            CAREERS AT HAVEN →
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage; 